import { useEffect, useMemo, useState } from "react";
import {
  bumpUnit, formatOffset, formatTimecode, framesToParts, timecodeAt,
  type ClockReading, type TimecodeUnit,
} from "@infoflyt/shared/timecode";
import { useExitDelay } from "../components/useExitDelay";
import { Wheel } from "../components/Wheel";
import { LiquidGlass } from "../components/LiquidGlass";
import { useSession } from "../state/SessionProvider";
import { readClock } from "../clock/clock";
import { heavy } from "../components/haptics";

const UNITS: readonly { unit: TimecodeUnit; label: string }[] = [
  { unit: "hours", label: "TT" },
  { unit: "minutes", label: "MM" },
  { unit: "seconds", label: "SS" },
  { unit: "frames", label: "FF" },
];

const p2 = (n: number): string => String(n).padStart(2, "0");

/**
 * Tidskodearket. Den viktigste inputen i appen — feil her koster en
 * opptaksdag.
 *
 * Verdien er absolutt, ikke en differanse: slik synkes eksterne tidskoder.
 * Man stiller inn en verdi noen sekunder fram i tid og bekrefter i det kilden
 * treffer den. Differansen vises ved siden av, ikke i stedet for, fordi det
 * ved justering i etterkant ikke finnes noen kilde å time mot — da er det
 * differansen som betyr noe.
 *
 * Se docs/13-design.md.
 */
export function TimecodeSheet({
  open, at, onClose,
}: {
  open: boolean;
  at: ClockReading;
  onClose(): void;
}): JSX.Element | null {
  const { anchor, spec, entries, adjustAnchorTo } = useSession();

  /**
   * ========  DET SOM BEKREFTES, ER DET SOM BLIR STAAENDE  ========
   *
   * Her laa et hopp brukeren fant 02.11.2026: «Naar jeg skal sette
   * tidskoden i appen, saa hopper den noen sekunder frem fra der jeg satt
   * tidskoden.» Og det gjorde den — noeyaktig saa mange sekunder som det
   * tok aa stille hjulene.
   *
   * Ankeret ble satt mot avlesningen arket ble AAPNET paa. Tidskoden
   * traff da verdien i det oeyeblikket, og hadde gaatt videre naar fingeren
   * kom til Bekreft. Begrunnelsen var forhaandsvisningen: differansen og
   * handlingen skulle regnes mot det samme. Men fila sier selv hvordan
   * dette brukes — «man stiller inn en verdi noen sekunder fram i tid og
   * bekrefter i det kilden treffer den» — og det er en klokke man stiller,
   * ikke et skjema man fyller ut. Da er det TRYKKET som er oeyeblikket.
   *
   * Forhaandsvisningen loeses ved aa la den gaa: `naa` er avlesningen i
   * dette bildet, differansen regnes mot den, og det som staar der er det
   * som skjer om du trykker naa. Ingen av tallene er laast — bortsett fra
   * `target`, som er det du har stilt inn, og som aldri roerer seg.
   */
  const [target, setTarget] = useState(0);
  const [active, setActive] = useState<TimecodeUnit>("seconds");
  /*
   * Arket skal gaa ut slik det kom inn.
   *
   * Foer returnerte komponenten null i samme oeyeblikk som `open` slo om, saa
   * arket steg opp over 320 ms og forsvant i ett bilde. 320 matcher .sheet
   * sin transform i overlays.css.
   */
  const { mounted, shown } = useExitDelay(open && anchor !== null, 320);

  useEffect(() => {
    if (!open || anchor === null) return;
    const frames = timecodeAt(anchor, at, spec).frames;
    // FF står på 00 som standard. Man sikter mot et helt sekund; frametallet
    // på en boks er ikke lesbart i fart.
    setTarget(frames - (frames % spec.rate.nominal));
    setActive("seconds");
    // Startverdien låses til åpningen: den skal ikke løpe under fingeren.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const parts = useMemo(
    () => (anchor === null ? null : framesToParts(target, spec)),
    [target, spec, anchor],
  );

  if (!mounted || anchor === null || parts === null) return null;

  /* Hva klokka staar paa I DETTE BILDET. Differansen under er mot denne,
     og det er den samme avlesningen Bekreft bruker. */
  const naa = timecodeAt(anchor, at, spec).frames;
  const delta = target - naa;
  const oldest = entries[0];
  const oldestFrames = oldest === undefined ? null : timecodeAt(anchor, oldest, spec).frames;

  const values: Record<TimecodeUnit, number> = {
    hours: parts.hours, minutes: parts.minutes, seconds: parts.seconds, frames: parts.frames,
  };
  const caps: Record<TimecodeUnit, number> = {
    hours: 24, minutes: 60, seconds: 60, frames: spec.rate.nominal,
  };

  return (
    <div className={`overlay${shown ? " overlay--in" : ""}`}>
      <button
        type="button"
        className="overlay__scrim"
        onClick={onClose}
        aria-label="Lukk tidskodearket"
      />
      <LiquidGlass className="sheet" grade="top">
        <div className="sheet__head">
          <span className="sheet__title">Juster tidskode</span>
          <span className="mono" style={{ fontSize: 12.5, color: "var(--ink-dim)" }}>
            Differanse <span style={{ color: "var(--ink)" }}>{formatOffset(delta, spec)}</span>
          </span>
        </div>

        <div className="sheet__wheels">
          {UNITS.map(({ unit, label }) => {
            const v = values[unit];
            const cap = caps[unit];
            return (
              <Wheel
                key={unit}
                label={label}
                size="small"
                value={p2(v)}
                prev={p2((v + 1) % cap)}
                next={p2((v - 1 + cap) % cap)}
                prev2={p2((v + 2) % cap)}
                next2={p2((v - 2 + cap) % cap)}
                active={active === unit}
                onFocus={() => setActive(unit)}
                onStep={(dir) => setTarget((t) => bumpUnit(t, unit, dir, spec))}
              />
            );
          })}
        </div>

        {/* Før bekreftelse vises hva postene flyttes fra og til, og hvor
            mange som berøres. */}
        <div className="sheet__preview">
          <span style={{ color: "var(--ink)" }}>
            {entries.length} {entries.length === 1 ? "post flyttes" : "poster flyttes"}
          </span>
          <span style={{ color: "var(--ink-dim)" }}>
            {oldestFrames === null ? "—" : formatTimecode(oldestFrames, spec)}
            {"  →  "}
            {oldestFrames === null ? "—" : formatTimecode(oldestFrames + delta, spec)}
          </span>
        </div>

        <button
          type="button"
          className="sheet__confirm"
          onClick={() => {
            // Bekreftelsen har et tyngre utslag enn hakkene.
            heavy();
            /*
             * Avlesningen tas HER, ikke fra `at`.
             *
             * `at` kommer fra klokketikket — tiendedels sekund, altsaa
             * opptil to-tre frames gammel naar fingeren treffer. Det er
             * smaatt og det er for mye: dette er stedet tidskoden settes,
             * og en frame er en frame. `readClock` er den samme kilden
             * tikket bruker.
             */
            adjustAnchorTo(target, readClock());
            onClose();
          }}
        >
          Bekreft
        </button>
        <button type="button" className="sheet__cancel" onClick={onClose}>
          Avbryt
        </button>
      </LiquidGlass>
    </div>
  );
}
